import "./FeatureBlock.scss";
import { FeatureBlockProps } from "/Models/Generated/FeatureBlockProps";
import { useState } from "react";
import GridRow from "/Components/Layout/GridRow/GridRow";
import {
  GridPresetOneHalf,
  Labels,
  OptimizelyEditProperty,
  Picture,
  VideoWithPoster,
} from "/App.Components";
import { createSpreadableBem } from "/Helpers/createBem";
import { DesignTheme } from "/App.GeneratedEnums";
import Tags from "../Tags/Tags";

const FeatureBlock = ({
  labels,
  title,
  titlePropertyName,
  description,
  descriptionPropertyName,
  image,
  video,
  link,
  leftAligned,
  categoryTags,
  theme,
}: FeatureBlockProps) => {
  const [posterClicked, setPosterClicked] = useState(false);
  const bem = createSpreadableBem("feature-block-norad");
  const hasVideo = !!video;
  const hasMedia = !!image || hasVideo;
  const WrapperComponent = link && link.href && !hasVideo ? "a" : "div";

  const ImageColumn = () =>
    hasMedia && (
      <GridPresetOneHalf mobileOrder={-1}>
        <div {...bem("__thumbnail")}>
          {video ? (
            <VideoWithPoster
              posterClicked={posterClicked}
              setPosterClicked={() => setPosterClicked(!posterClicked)}
              {...video}
              posterBehaviour="Cover"
              posterAspectRatio="16/9"
            />
          ) : (
            image && (
              <Picture
                {...image}
                pictureAspectRatio="3/2"
                pictureBehaviour="Cover"
                noAlt={image.altText === ""}
              />
            )
          )}
        </div>
      </GridPresetOneHalf>
    );

  const TextColumn = () => {
    return (
      <GridPresetOneHalf>
        <div {...bem("__text-container")}>
          <div {...bem("__text")}>
            {labels && theme !== DesignTheme.Insight && (
              <div {...bem("__labels")}>
                <Labels {...labels} />
              </div>
            )}
            <OptimizelyEditProperty propertyName={titlePropertyName}>
              {title && (
                <h2
                  {...bem("__heading", {
                    "--insight": theme === DesignTheme.Insight,
                  })}
                >
                  <span>{title}</span>
                </h2>
              )}
            </OptimizelyEditProperty>
            <OptimizelyEditProperty propertyName={descriptionPropertyName}>
              {description && (
                <p
                  {...bem("__lead-text", {
                    "--insight": theme === DesignTheme.Insight,
                  })}
                >
                  {description}
                </p>
              )}
            </OptimizelyEditProperty>
            {theme === DesignTheme.Insight && categoryTags && (
              <div {...bem("__tags")}>
                <Tags {...categoryTags} />
              </div>
            )}
          </div>
        </div>
      </GridPresetOneHalf>
    );
  };

  const FirstColumn = !leftAligned ? ImageColumn : TextColumn;
  const SecondColumn = !leftAligned ? TextColumn : ImageColumn;

  return (
    <WrapperComponent
      {...bem("", {
        "--left-aligned": leftAligned,
        "--link": !hasVideo && link && link.href,
        "--link-insight":
          !hasVideo && link && link.href && theme === DesignTheme.Insight,
      })}
      {...(link && link.href ? { href: link.href, target: link.target } : {})}
    >
      <div
        {...bem("__content", {
          "--no-media": !hasMedia,
          "--insight": theme === DesignTheme.Insight,
        })}
      >
        <GridRow>
          <FirstColumn />
          <SecondColumn />
        </GridRow>
      </div>
    </WrapperComponent>
  );
};

export default FeatureBlock;
